/*
 * Raiman Rocks product and cart sizing custom elements.
 * Kept separate from the Prestige vendor bundle so sizing changes do not
 * invalidate or create merge conflicts across the full theme runtime.
 */

var PRODUCT_SIZING_INLINE_DURATION = 900;
var PRODUCT_SIZING_INLINE_CONTENT_DURATION = 420;
var PRODUCT_SIZING_INLINE_TIMER_BUFFER = 40;

function rrFadeSizingSentence(owner, selectedValue, animate) {
  const current = owner.finalSaleMessages.find((message) => !message.hidden);
  const next = owner.finalSaleMessages.find((message) => message.dataset.sizingFinalSaleMessage === selectedValue);
  const outgoing = current?.querySelector(".product-sizing-dialog__selected-size");
  const opacity = outgoing ? getComputedStyle(outgoing).opacity : "1";
  const token = owner.sizingSentenceToken = (owner.sizingSentenceToken || 0) + 1;
  owner.sizingSentenceAnimation?.cancel();
  owner.sizingSentenceAnimation = null;
  if (!animate || owner.dataset.sizingType !== "ring" || !outgoing || !next ||
      owner.finalSaleNotice?.hidden || !owner.finalSaleNotice?.classList.contains("is-visible") ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  const fadeIn = () => {
    if (owner.sizingSentenceToken !== token || !owner.isConnected) return;
    owner.finalSaleMessages.forEach((message) => { message.hidden = message !== next; });
    const incoming = next.querySelector(".product-sizing-dialog__selected-size");
    if (!incoming) return;
    const animation = incoming.animate([{ opacity: current === next ? opacity : 0 }, { opacity: 1 }],
      { duration: 150, easing: "ease-out" });
    owner.sizingSentenceAnimation = animation;
    animation.finished.then(() => {
      if (owner.sizingSentenceToken === token) owner.sizingSentenceAnimation = null;
    }).catch(() => {});
  };
  if (current === next) {
    fadeIn();
  } else {
    const animation = outgoing.animate([{ opacity }, { opacity: 0 }],
      { duration: 150, easing: "ease-in", fill: "forwards" });
    owner.sizingSentenceAnimation = animation;
    animation.finished.then(() => {
      if (owner.sizingSentenceToken !== token || !owner.isConnected) return;
      animation.cancel();
      fadeIn();
    }).catch(() => {});
  }
  return true;
}

var rrCartSizingOrder = (() => {
  const storageKey = 'rr-cart-drawer-sizing-order';
  let order = [];
  let pendingKey = null;
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
    if (Array.isArray(saved)) order = saved.filter(key => typeof key === 'string');
  } catch {}
  const save = () => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(order)); } catch {}
  };
  const apply = () => {
    const drawer = document.querySelector('cart-drawer');
    const container = drawer?.querySelector('.cart-drawer__items');
    if (!container) return;
    const items = [...container.querySelectorAll(':scope > line-item[data-line-key]')];
    const byKey = new Map(items.map(item => [item.getAttribute('data-line-key'), item]));
    const known = order.filter(key => byKey.has(key));
    const added = [...byKey.keys()].filter(key => !known.includes(key));
    order = [...added, ...known];
    if (pendingKey && byKey.has(pendingKey)) order = [pendingKey, ...order.filter(key => key !== pendingKey)];
    const current = items.map(item => item.getAttribute('data-line-key'));
    if (order.some((key, index) => key !== current[index])) {
      order.forEach(key => container.appendChild(byKey.get(key)));
    }
    if (pendingKey && byKey.has(pendingKey)) {
      const body = drawer.shadowRoot?.querySelector('[part~="body"]');
      if (body) body.scrollTop = 0;
      pendingKey = null;
    }
    save();
  };
  document.addEventListener('cart-drawer:refreshed', apply);
  document.addEventListener('shopify:section:load', apply);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, { once: true });
  else apply();
  return {
    promote(cart, previousItem, properties) {
      const entries = Object.entries(properties);
      const updated = cart.items?.find(item => Number(item.variant_id) === Number(previousItem.variant_id)
        && item.selling_plan_allocation?.selling_plan?.id === previousItem.selling_plan_allocation?.selling_plan?.id
        && Object.keys(item.properties || {}).length === entries.length
        && entries.every(([key, value]) => item.properties?.[key] === value));
      if (!updated) return;
      const current = [...document.querySelectorAll('cart-drawer .cart-drawer__items > line-item[data-line-key]')]
        .map(item => item.getAttribute('data-line-key'));
      order = [updated.key, ...(current.length ? current : order).filter(key => key !== previousItem.key && key !== updated.key)];
      pendingKey = updated.key;
      save();
    }
  };
})();

var ProductSizingModal = class extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.dialog = null;
    this.form = null;
    this.choiceInputs = [];
    this.confirmedChoice = null;
    this.confirmButton = null;
    this.titleElement = null;
    this.errorMessage = null;
    this.sizingPropertyInput = null;
    this.assistancePropertyInput = null;
    this.finalSaleNotice = null;
    this.finalSaleMessages = [];
    this.finalSaleNoticeHideTimer = null;
    this.finalSaleNoticeShouldBeVisible = false;
    this.submitter = null;
    this.mode = "add";
    this.allowNextSubmit = false;
    this.isClosing = false;
    this.isUpdating = false;
    this.inlineState = "closed";
    this.buyButtons = null;
    this.buttonStateObserver = null;
    this.inlineOpener = null;
    this.inlineButton = null;
    this.inlineTriggerText = null;
    this.inlineMorphTitle = null;
    this.inlineTitleMorphStartTransform = "";
    this.inlineTimer = null;
    this.inlineAnchorTimer = null;
    this.inlineCloseFadeTimer = null;
    this.inlineCloseResolver = null;
    this.inlineCloseFrameSettled = false;
    this.inlineCloseFrameCommitted = false;
    this.inlineCloseAnchorSettled = false;
    this.inlineCloseSubmitAfterClose = false;
    this.inlineCloseAlignment = "top";
    this.inlineCloseCommitRequested = false;
    this.inlineCloseCommitPromise = null;
    this.inlineCloseCommitResolver = null;
    this.inlineCloseTargetHeight = null;
    this.inlineCloseTargetWidth = null;
    this.inlineClosedFrameHeight = null;
    this.inlineClosedFrameWidth = null;
    this.giaReport = null;
    this.giaRevealPending = false;
    this.giaRevealWasAdding = false;
    this.giaRevealFrame = null;
    this.giaRevealSecondFrame = null;
    this.giaRevealTimer = null;
    this.updateLineKey = null;
  }
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    this.dialog = this.querySelector("dialog");
    this.form = this.closest("form");
    this.choiceInputs = Array.from(this.querySelectorAll("[data-sizing-size], [data-sizing-assistance]"));
    this.confirmedChoice = this.choiceInputs.find((input) => input.checked) || null;
    this.confirmButton = this.querySelector("[data-sizing-confirm]");
    this.titleElement = this.querySelector("[data-sizing-title]");
    this.errorMessage = this.querySelector("[data-sizing-error]");
    this.sizingPropertyInput = this.form?.querySelector("[data-sizing-property]");
    this.assistancePropertyInput = this.form?.querySelector("[data-sizing-assistance-property]");
    this.finalSaleNotice = this.querySelector("[data-sizing-final-sale-notice]");
    this.finalSaleMessages = Array.from(this.querySelectorAll("[data-sizing-final-sale-message]"));
    this.giaReport = this.closest(".product-info")?.querySelector("[data-rr-gia-report]") || null;
    if (this.isInlinePresentation) {
      this.setupInlinePresentation();
    }
    this.choiceInputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.updateFinalSaleNoticeState(true);
        this.updateConfirmState();
      }, { signal });
    });
    this.querySelector("[data-sizing-close]")?.addEventListener("click", () => this.close(), { signal });
    this.confirmButton?.addEventListener("click", () => this.confirmSelection(), { signal });
    document.documentElement.addEventListener("rr:sizing-selection-committed", (event) => {
      if (Number(event.detail?.variantId) === this.currentVariantId) {
        this.revealGiaReport();
      }
    }, { signal });
    this.dialog?.addEventListener("cancel", (event) => {
      event.preventDefault();
    }, { signal });
    if (!this.isInlinePresentation) {
      this.dialog?.addEventListener("click", (event) => {
        if (event.target === this.dialog) {
          this.close();
        }
      }, { signal });
    }
    this.setMode("add");
    this.updateFinalSaleNoticeState();
    this.updateConfirmState();
    this.openFromCartHandoff();
  }
  disconnectedCallback() {
    this.sizingSentenceToken = (this.sizingSentenceToken || 0) + 1;
    this.sizingSentenceAnimation?.cancel();
    this.abortController?.abort();
    this.buttonStateObserver?.disconnect();
    this.buttonStateObserver = null;
    this.clearInlineTimer();
    this.clearGiaRevealAnimation();
    if (this.finalSaleNoticeHideTimer) {
      window.clearTimeout(this.finalSaleNoticeHideTimer);
    }
    if (this.isInlinePresentation) {
      this.resetInlineTitleMorph();
      this.dialog?.removeAttribute("open");
    } else if (this.dialog?.open) {
      this.dialog.close();
    }
  }
  get isInlinePresentation() {
    return this.dataset.presentation === "inline";
  }
  setupInlinePresentation() {
    if (!this.dialog || !this.form) {
      return;
    }
    this.buyButtons = this.form.querySelector("buy-buttons[data-loading-text]");
    this.inlineButton = this.buyButtons?.querySelector(':scope > button[type="submit"]') || null;
    this.inlineTriggerText = this.inlineButton?.querySelector(".button__state-content") || this.inlineButton;
    this.inlineMorphTitle = this.querySelector("[data-sizing-morph-title]");
    this.inlineOpener = this.buyButtons?.parentElement || null;
    if (this.inlineOpener && !this.contains(this.inlineOpener)) {
      this.inlineOpener.classList.add("product-sizing-inline__opener");
      const inlineInsertionReference = [this.inlineMorphTitle, this.dialog]
        .find((element) => element?.parentElement === this) || null;
      this.insertBefore(this.inlineOpener, inlineInsertionReference);
    }
    if (this.inlineMorphTitle && this.titleElement) {
      this.inlineMorphTitle.textContent = this.titleElement.textContent;
    }
    this.dialog.setAttribute("open", "");
    this.dialog.setAttribute("aria-hidden", "true");
    this.dialog.setAttribute("inert", "");
    if (this.inlineButton && this.dialog.id) {
      this.inlineButton.setAttribute("aria-controls", this.dialog.id);
      this.inlineButton.setAttribute("aria-expanded", "false");
    }
    if (this.buyButtons) {
      this.buttonStateObserver?.disconnect();
      this.buttonStateObserver = new MutationObserver(() => this.handleInlineButtonState());
      this.buttonStateObserver.observe(this.buyButtons, {
        attributes: true,
        attributeFilter: ["data-cart-button-state"]
      });
      this.handleInlineButtonState();
    }
  }
  get currentVariantId() {
    return Number(this.form?.querySelector('[name="id"]')?.value);
  }
  handleInlineButtonState() {
    const state = this.buyButtons?.getAttribute("data-cart-button-state") || "default";
    if (state === "adding" && this.giaRevealPending) {
      this.giaRevealWasAdding = true;
      return;
    }
    if (state === "added" && this.giaRevealPending) {
      this.giaRevealPending = false;
      this.giaRevealWasAdding = false;
      this.revealGiaReport();
      return;
    }
    if (state === "default" && this.giaRevealPending && this.giaRevealWasAdding) {
      this.giaRevealPending = false;
      this.giaRevealWasAdding = false;
    }
  }
  clearGiaRevealAnimation() {
    if (this.giaRevealFrame !== null) {
      cancelAnimationFrame(this.giaRevealFrame);
      this.giaRevealFrame = null;
    }
    if (this.giaRevealSecondFrame !== null) {
      cancelAnimationFrame(this.giaRevealSecondFrame);
      this.giaRevealSecondFrame = null;
    }
    if (this.giaRevealTimer !== null) {
      window.clearTimeout(this.giaRevealTimer);
      this.giaRevealTimer = null;
    }
  }
  revealGiaReport() {
    if (!this.giaReport) {
      return;
    }
    this.clearGiaRevealAnimation();
    this.giaReport.classList.remove("is-sizing-revealing");
    this.giaReport.classList.add("is-sizing-reveal-preparing");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      this.giaReport.classList.remove("is-sizing-reveal-preparing");
      return;
    }
    this.giaReport.getBoundingClientRect();
    this.giaRevealFrame = requestAnimationFrame(() => {
      this.giaRevealFrame = null;
      this.giaRevealSecondFrame = requestAnimationFrame(() => {
        this.giaRevealSecondFrame = null;
        this.giaReport?.classList.remove("is-sizing-reveal-preparing");
        this.giaReport?.classList.add("is-sizing-revealing");
        this.giaRevealTimer = window.setTimeout(() => {
          this.giaReport?.classList.remove("is-sizing-revealing");
          this.giaRevealTimer = null;
        }, 760);
      });
    });
  }
  clearInlineTimer() {
    this.dialog?.style.removeProperty("width");
    if (this.inlineTimer !== null) {
      window.clearTimeout(this.inlineTimer);
      this.inlineTimer = null;
    }
    if (this.inlineAnchorTimer !== null) {
      window.clearTimeout(this.inlineAnchorTimer);
      this.inlineAnchorTimer = null;
    }
    if (this.inlineCloseFadeTimer !== null) {
      window.clearTimeout(this.inlineCloseFadeTimer);
      this.inlineCloseFadeTimer = null;
    }
  }
  get inlineFrameBorderHeight() {
    const styles = window.getComputedStyle(this);
    return (Number.parseFloat(styles.borderTopWidth) || 0) +
      (Number.parseFloat(styles.borderBottomWidth) || 0);
  }
  get inlineFrameBorderWidth() {
    const styles = window.getComputedStyle(this);
    return (Number.parseFloat(styles.borderLeftWidth) || 0) +
      (Number.parseFloat(styles.borderRightWidth) || 0);
  }
  measureInlineCollapsedFrame() {
    const fallbackHeight = this.inlineClosedFrameHeight || Math.ceil(this.getBoundingClientRect().height);
    const fallbackWidth = this.inlineClosedFrameWidth || Math.ceil(this.getBoundingClientRect().width);
    if (!this.inlineOpener || !this.inlineButton) {
      return { height: fallbackHeight, width: fallbackWidth };
    }
    const currentContent = this.inlineButton.querySelector(".button__state-content");
    const contentRect = currentContent?.getBoundingClientRect();
    const buttonRect = this.inlineButton.getBoundingClientRect();
    const buttonStyles = window.getComputedStyle(this.inlineButton);
    const parseLength = (value) => Number.parseFloat(value) || 0;
    const buttonInlineChrome =
      parseLength(buttonStyles.paddingInlineStart || buttonStyles.paddingLeft) +
      parseLength(buttonStyles.paddingInlineEnd || buttonStyles.paddingRight) +
      parseLength(buttonStyles.borderInlineStartWidth || buttonStyles.borderLeftWidth) +
      parseLength(buttonStyles.borderInlineEndWidth || buttonStyles.borderRightWidth);
    const measuredWidth = contentRect?.width > 0
      ? contentRect.width + buttonInlineChrome + this.inlineFrameBorderWidth
      : 0;
    const measuredHeight = buttonRect.height > 0
      ? buttonRect.height + this.inlineFrameBorderHeight
      : 0;
    return {
      height: this.inlineClosedFrameHeight || measuredHeight || fallbackHeight,
      width: measuredWidth > buttonInlineChrome
        ? measuredWidth
        : fallbackWidth
    };
  }
  get inlineExpandedFrameWidth() {
    const owningBlock = this.closest('[data-block-type="buy-buttons"]');
    const availableWidth = owningBlock?.getBoundingClientRect().width ||
      this.parentElement?.getBoundingClientRect().width ||
      this.getBoundingClientRect().width;
    return Math.ceil(availableWidth);
  }
  prepareInlineTitleMorph(startRootRect, startLabelRect, targetRootRect) {
    if (!this.inlineMorphTitle || !this.titleElement || !startLabelRect) {
      return;
    }
    const targetTitleRect = this.titleElement.getBoundingClientRect();
    if (!startLabelRect.width || !targetTitleRect.width) {
      return;
    }
    const styles = window.getComputedStyle(this);
    const borderLeft = Number.parseFloat(styles.borderLeftWidth) || 0;
    const borderTop = Number.parseFloat(styles.borderTopWidth) || 0;
    const startLeft = startLabelRect.left - startRootRect.left - borderLeft;
    const startTop = startLabelRect.top - startRootRect.top - borderTop;
    const targetLeft = targetTitleRect.left - targetRootRect.left - borderLeft;
    const targetTop = targetTitleRect.top - targetRootRect.top - borderTop;
    const translateX = startLeft - targetLeft;
    const translateY = startTop - targetTop;
    const scale = startLabelRect.width / targetTitleRect.width;
    this.inlineTitleMorphStartTransform =
      `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    this.inlineMorphTitle.classList.add("is-preparing");
    this.inlineMorphTitle.style.left = `${targetLeft}px`;
    this.inlineMorphTitle.style.top = `${targetTop}px`;
    this.inlineMorphTitle.style.width = `${targetTitleRect.width}px`;
    this.inlineMorphTitle.style.transform = this.inlineTitleMorphStartTransform;
    this.inlineMorphTitle.classList.add("is-active");
  }
  resetInlineTitleMorph() {
    this.inlineTitleMorphStartTransform = "";
    if (!this.inlineMorphTitle) {
      return;
    }
    this.inlineMorphTitle.classList.add("is-preparing");
    this.inlineMorphTitle.classList.remove("is-active");
    this.inlineMorphTitle.style.left = "";
    this.inlineMorphTitle.style.top = "";
    this.inlineMorphTitle.style.width = "";
    this.inlineMorphTitle.style.transform = "";
    this.inlineMorphTitle.getBoundingClientRect?.();
    this.inlineMorphTitle.classList.remove("is-preparing");
  }
  prepareInlineAddingState() {
    if (!this.isInlinePresentation || !this.inlineButton) {
      return;
    }
    const buyButtons = this.inlineButton.closest("buy-buttons[data-loading-text]");
    if (!buyButtons || buyButtons.getAttribute("data-cart-button-state") === "adding") {
      return;
    }
    const content = document.createElement("span");
    content.className = "button__state-content";
    const spinner = document.createElement("span");
    spinner.className = "button__state-spinner";
    spinner.setAttribute("aria-hidden", "true");
    const label = document.createElement("span");
    label.textContent = buyButtons.getAttribute("data-loading-text") || "";
    content.append(spinner, label);
    this.inlineButton.replaceChildren(content);
    buyButtons.setAttribute("data-cart-button-state", "adding");
    this.inlineButton.setAttribute("aria-label", content.textContent.trim());
  }
  interceptSubmission(submitter) {
    if (this.allowNextSubmit) {
      this.allowNextSubmit = false;
      return false;
    }
    if (!this.dialog) {
      return false;
    }
    this.open(submitter, "add");
    return true;
  }
  async openForUpdate(submitter, lineKey = null) {
    if (!this.dialog || this.isUpdating) {
      return;
    }
    this.updateLineKey = lineKey || null;
    let currentLineItem = null;
    try {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      if (cartResponse.ok) {
        const currentCart = await cartResponse.json();
        const variantId = Number(this.form?.querySelector('[name="id"]')?.value);
        currentLineItem = this.updateLineKey
          ? currentCart.items.find((item) => item.key === this.updateLineKey) || null
          : currentCart.items.find((item) => Number(item.variant_id) === variantId) || null;
      }
    } catch (error) {
      console.error("[Product sizing editor]", error);
    }
    if (currentLineItem) {
      this.updateLineKey = currentLineItem.key;
      this.restoreChoiceFromProperties(currentLineItem.properties || {});
    } else {
      this.confirmedChoice = this.choiceInputs.find((input) => input.checked) || null;
    }
    this.open(submitter, "update");
  }
  openFromCartHandoff() {
    if (!this.isInlinePresentation || window.themeVariables?.settings?.pageType !== "product") {
      return;
    }
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("rr_sizing") !== "update") {
      return;
    }
    const requestedVariantId = Number(currentUrl.searchParams.get("variant"));
    if (requestedVariantId && requestedVariantId !== this.currentVariantId) {
      return;
    }
    const lineKey = currentUrl.searchParams.get("rr_sizing_key") || null;
    currentUrl.searchParams.delete("rr_sizing");
    currentUrl.searchParams.delete("rr_sizing_key");
    window.history.replaceState(
      window.history.state,
      "",
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
    );
    requestAnimationFrame(() => this.openForUpdate(this.inlineButton, lineKey));
  }
  open(submitter, mode = "add") {
    this.submitter = submitter;
    this.isClosing = false;
    if (mode !== "update") {
      this.updateLineKey = null;
    }
    this.setMode(mode);
    if (this.mode === "update") {
      this.restoreConfirmedChoice();
    }
    if (this.errorMessage) {
      this.errorMessage.hidden = true;
    }
    this.updateFinalSaleNoticeState();
    this.updateConfirmState();
    if (this.isInlinePresentation) {
      this.requestInlineOpen();
      return;
    }
    if (!this.dialog.open) {
      this.dialog.showModal();
    }
    this.dialog.scrollTop = 0;
    requestAnimationFrame(() => {
      this.dialog.classList.add("is-visible");
      const selectedChoice = this.choiceInputs.find((input) => input.checked);
      (selectedChoice || this.choiceInputs[0])?.focus({ preventScroll: true });
    });
  }
  requestInlineOpen() {
    if (!this.dialog || this.inlineState === "opening" || this.inlineState === "open") {
      return;
    }
    let activated = false;
    const activate = (coordinated = false) => {
      if (activated) {
        return;
      }
      activated = true;
      return this.expandInlineFrame(coordinated);
    };
    const onComplete = () => this.focusInlineContent();
    const anchorRequest = new CustomEvent("rr:product-anchor-open", {
      bubbles: true,
      cancelable: true,
      detail: {
        anchor: this,
        alignment: "top",
        proportionalOpen: true,
        activate,
        onComplete
      }
    });
    const handledByProductDetails = !this.dispatchEvent(anchorRequest);
    if (!handledByProductDetails) {
      activate();
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start"
      });
      window.setTimeout(onComplete, reduceMotion ? 0 : PRODUCT_SIZING_INLINE_DURATION);
    }
  }
  expandInlineFrame(coordinated = false) {
    if (!this.dialog || !this.inlineOpener) {
      return;
    }
    this.clearInlineTimer();
    this.inlineState = "opening";
    const startRect = this.getBoundingClientRect();
    const startHeight = Math.ceil(startRect.height);
    const startWidth = Math.ceil(startRect.width);
    this.inlineClosedFrameHeight = startHeight;
    this.inlineClosedFrameWidth = startWidth;
    const targetWidth = this.inlineExpandedFrameWidth;
    this.resetInlineTitleMorph();
    this.dialog.scrollTop = 0;
    this.style.height = `${startHeight}px`;
    this.style.width = `${startWidth}px`;
    this.getBoundingClientRect();
    this.classList.remove("is-inline-open", "is-inline-closing");
    this.classList.add("is-inline-opening");
    this.dialog.setAttribute("aria-hidden", "true");
    this.inlineButton?.setAttribute("aria-expanded", "true");
    this.classList.add("is-inline-measuring");
    this.style.width = `${targetWidth}px`;
    this.dialog.style.width = `${targetWidth - this.inlineFrameBorderWidth}px`;
    const targetHeight = Math.ceil(this.dialog.scrollHeight + this.inlineFrameBorderHeight);
    this.style.width = `${startWidth}px`;
    this.getBoundingClientRect();
    this.classList.remove("is-inline-measuring");
    if (coordinated) {
      return {
        height: targetHeight,
        width: targetWidth,
        finish: () => this.finishInlineOpen()
      };
    }
    requestAnimationFrame(() => {
      if (this.inlineState !== "opening") {
        return;
      }
      this.style.width = `${targetWidth}px`;
      this.style.height = `${targetHeight}px`;
    });
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.inlineTimer = window.setTimeout(
      () => this.finishInlineOpen(),
      reduceMotion ? 0 : PRODUCT_SIZING_INLINE_DURATION + PRODUCT_SIZING_INLINE_TIMER_BUFFER
    );
  }
  finishInlineOpen() {
    if (this.inlineState !== "opening") {
      return;
    }
    this.clearInlineTimer();
    this.inlineState = "open";
    this.classList.remove("is-inline-opening");
    this.classList.add("is-inline-open");
    this.style.width = "100%";
    this.style.height = "auto";
    this.dialog?.removeAttribute("inert");
    this.dialog?.setAttribute("aria-hidden", "false");
  }
  focusInlineContent() {
    if (this.inlineState === "opening") {
      this.finishInlineOpen();
    }
    if (this.inlineState !== "open") {
      return;
    }
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    (selectedChoice || this.choiceInputs[0])?.focus({ preventScroll: true });
  }
  setMode(mode) {
    this.mode = mode === "update" ? "update" : "add";
    const nextTitle = this.mode === "update" ? this.dataset.updateTitle : this.dataset.selectTitle;
    const nextLabel = this.mode === "update" ? this.dataset.updateLabel : this.dataset.addLabel;
    if (this.titleElement && nextTitle) {
      this.titleElement.textContent = nextTitle;
    }
    if (this.inlineMorphTitle && nextTitle) {
      this.inlineMorphTitle.textContent = nextTitle;
    }
    if (this.confirmButton && nextLabel) {
      this.confirmButton.textContent = nextLabel;
    }
  }
  restoreChoiceFromProperties(properties) {
    const assistanceRequested = properties["Sizing Assistance"] || properties["Sizing assistance"];
    const storedSize = this.dataset.sizingType === "bracelet" ? properties["Bracelet Size"] || properties["Requested Bracelet Size"] || properties["Request Bracelet Size"] || properties["Requested wrist size"] : properties["Ring Size"] || properties["Requested Ring Size"] || properties["Requested ring size"];
    const selectedValue = this.dataset.sizingType === "bracelet" ? String(storedSize || "").replace(/\s*in$/i, "") : String(storedSize || "").replace(/^US\s+/i, "");
    const confirmedChoice = assistanceRequested ? this.choiceInputs.find((input) => input.hasAttribute("data-sizing-assistance")) : this.choiceInputs.find((input) => input.hasAttribute("data-sizing-size") && input.value === selectedValue);
    this.confirmedChoice = confirmedChoice || null;
    this.restoreConfirmedChoice();
  }
  restoreConfirmedChoice() {
    this.choiceInputs.forEach((input) => {
      input.checked = input === this.confirmedChoice;
    });
    this.updateFinalSaleNoticeState();
    this.updateConfirmState();
  }
  close(submitAfterClose = false) {
    if (this.isInlinePresentation) {
      return this.requestInlineClose(submitAfterClose);
    }
    if (!this.dialog?.open || this.isClosing) {
      return Promise.resolve();
    }
    this.isClosing = true;
    this.dialog.classList.remove("is-visible");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return new Promise((resolve) => {
      window.setTimeout(() => {
        this.dialog.close();
        this.isClosing = false;
        this.completeClose(submitAfterClose);
        resolve();
      }, reduceMotion ? 0 : 180);
    });
  }
  resolveInlineCloseAlignment() {
    const detailsAccordion = this.closest(".product-info")?.querySelector(".rr-details__accordion");
    return detailsAccordion?.hasAttribute("open") ? "bottom" : "page-top";
  }
  requestInlineClose(submitAfterClose = false) {
    if (this.inlineState === "closed" || this.inlineState === "closing") {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.inlineCloseResolver = resolve;
      this.inlineCloseFrameSettled = false;
      this.inlineCloseFrameCommitted = false;
      this.inlineCloseAnchorSettled = false;
      this.inlineCloseSubmitAfterClose = submitAfterClose;
      this.inlineCloseAlignment = submitAfterClose && window.matchMedia("(max-width: 999px)").matches
        ? "bottom"
        : this.resolveInlineCloseAlignment();
      this.inlineCloseCommitRequested = false;
      this.inlineCloseCommitPromise = new Promise((commitResolve) => {
        this.inlineCloseCommitResolver = commitResolve;
      });
      if (!this.beginInlineCloseFade()) {
        return;
      }
      const dispatchAnchorClose = () => {
        this.inlineCloseFadeTimer = null;
        let activated = false;
        const activate = () => {
          if (activated) {
            return;
          }
          activated = true;
          this.collapseInlineFrame(submitAfterClose);
        };
        const onComplete = () => {
          this.inlineCloseAnchorSettled = true;
          this.finishInlineCloseWhenSettled();
        };
        const commit = () => this.commitInlineCloseFrame();
        const anchorRequest = new CustomEvent("rr:product-anchor-close", {
          bubbles: true,
          cancelable: true,
          detail: {
            anchor: this,
            alignment: this.inlineCloseAlignment,
            activate,
            commit,
            onComplete
          }
        });
        const handledByProductDetails = !this.dispatchEvent(anchorRequest);
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (!handledByProductDetails) {
          activate();
          if (this.inlineCloseAlignment === "page-top") {
            window.scrollTo({
              top: 0,
              behavior: reduceMotion ? "auto" : "smooth"
            });
          }
          commit().then(onComplete);
        } else {
          this.inlineAnchorTimer = window.setTimeout(() => {
            this.inlineAnchorTimer = null;
            commit().then(onComplete);
          }, reduceMotion ? 0 : PRODUCT_SIZING_INLINE_DURATION + 800);
        }
      };
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.inlineCloseFadeTimer = window.setTimeout(
        dispatchAnchorClose,
        reduceMotion ? 0 : PRODUCT_SIZING_INLINE_CONTENT_DURATION
      );
    });
  }
  beginInlineCloseFade() {
    if (!this.dialog || !this.inlineOpener) {
      this.inlineState = "closing";
      this.inlineCloseFrameSettled = true;
      this.inlineCloseFrameCommitted = true;
      this.inlineCloseAnchorSettled = true;
      this.inlineCloseCommitResolver?.();
      this.finishInlineCloseWhenSettled();
      return false;
    }
    this.clearInlineTimer();
    this.isClosing = true;
    this.inlineState = "closing";
    const startRect = this.getBoundingClientRect();
    const collapsedFrame = this.measureInlineCollapsedFrame();
    this.inlineCloseTargetHeight = collapsedFrame.height;
    this.inlineCloseTargetWidth = collapsedFrame.width;
    this.classList.add("is-inline-measuring");
    this.style.height = `${Math.ceil(startRect.height)}px`;
    this.style.width = `${Math.ceil(startRect.width)}px`;
    this.getBoundingClientRect();
    this.classList.remove("is-inline-opening", "is-inline-open");
    this.classList.add("is-inline-closing");
    this.getBoundingClientRect();
    this.classList.remove("is-inline-measuring");
    this.getBoundingClientRect();
    this.dialog.setAttribute("inert", "");
    this.dialog.setAttribute("aria-hidden", "true");
    this.inlineButton?.setAttribute("aria-expanded", "false");
    return true;
  }
  collapseInlineFrame(submitAfterClose = false) {
    if (!this.dialog || !this.inlineOpener) {
      this.finishInlineClose(submitAfterClose);
      return;
    }
    this.clearInlineTimer();
    let targetHeight = this.inlineCloseTargetHeight;
    let targetWidth = this.inlineCloseTargetWidth;
    if (targetHeight === null || targetWidth === null) {
      const collapsedFrame = this.measureInlineCollapsedFrame();
      targetHeight ??= collapsedFrame.height;
      targetWidth ??= collapsedFrame.width;
    }
    this.style.width = `${targetWidth}px`;
    this.style.height = `${targetHeight}px`;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.inlineTimer = window.setTimeout(
      () => {
        this.inlineTimer = null;
        this.inlineCloseFrameSettled = true;
        if (this.inlineCloseCommitRequested) {
          this.applyInlineCloseFrameCommit();
        }
        this.finishInlineCloseWhenSettled();
      },
      reduceMotion ? 0 : PRODUCT_SIZING_INLINE_DURATION + PRODUCT_SIZING_INLINE_TIMER_BUFFER
    );
  }
  commitInlineCloseFrame() {
    this.inlineCloseCommitRequested = true;
    if (this.inlineCloseFrameSettled) {
      this.applyInlineCloseFrameCommit();
    }
    return this.inlineCloseCommitPromise || Promise.resolve();
  }
  applyInlineCloseFrameCommit() {
    if (this.inlineCloseFrameCommitted) {
      return;
    }
    this.classList.remove(
      "is-inline-opening",
      "is-inline-open",
      "is-inline-closing",
      "is-inline-measuring"
    );
    this.resetInlineTitleMorph();
    this.style.removeProperty("height");
    this.style.removeProperty("width");
    this.dialog?.setAttribute("inert", "");
    this.dialog?.setAttribute("aria-hidden", "true");
    this.inlineCloseFrameCommitted = true;
    const resolveCommit = this.inlineCloseCommitResolver;
    this.inlineCloseCommitResolver = null;
    resolveCommit?.();
  }
  finishInlineCloseWhenSettled() {
    if (
      this.inlineState !== "closing" ||
      !this.inlineCloseFrameSettled ||
      !this.inlineCloseFrameCommitted ||
      !this.inlineCloseAnchorSettled
    ) {
      return;
    }
    this.finishInlineClose(this.inlineCloseSubmitAfterClose);
  }
  finishInlineClose(submitAfterClose = false) {
    this.closest("safe-sticky.product-info")?.style.removeProperty("--rr-sizing-sticky-top");
    if (this.inlineState !== "closing") {
      return;
    }
    this.clearInlineTimer();
    this.applyInlineCloseFrameCommit();
    this.inlineState = "closed";
    this.isClosing = false;
    this.inlineCloseFrameSettled = false;
    this.inlineCloseFrameCommitted = false;
    this.inlineCloseAnchorSettled = false;
    this.inlineCloseSubmitAfterClose = false;
    this.inlineCloseAlignment = "top";
    this.inlineCloseCommitRequested = false;
    this.inlineCloseCommitPromise = null;
    this.inlineCloseCommitResolver = null;
    this.inlineCloseTargetHeight = null;
    this.inlineCloseTargetWidth = null;
    this.completeClose(submitAfterClose);
    const resolve = this.inlineCloseResolver;
    this.inlineCloseResolver = null;
    resolve?.();
  }
  completeClose(submitAfterClose = false) {
    if (!submitAfterClose && this.mode === "update") {
      this.restoreConfirmedChoice();
    }
    if (submitAfterClose && this.form) {
      this.allowNextSubmit = true;
      const submitter = this.submitter?.form === this.form ? this.submitter : this.form.querySelector('button[type="submit"]');
      if (submitter) {
        this.form.requestSubmit(submitter);
      } else {
        this.form.requestSubmit();
      }
    } else {
      this.submitter?.focus({ preventScroll: true });
    }
  }
  updateConfirmState() {
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    if (this.confirmButton) {
      this.confirmButton.disabled = this.isUpdating || !selectedChoice;
    }
  }
  isCustomSizeChoice(choice) {
    return Boolean(choice) && !choice.hasAttribute("data-sizing-assistance") && choice.value !== this.dataset.standardSize;
  }
  updateFinalSaleNoticeState(animate = false) {
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    const noticeVisible = this.isCustomSizeChoice(selectedChoice);
    const selectedValue = noticeVisible ? selectedChoice.value : "";
    if (rrFadeSizingSentence(this, selectedValue, animate)) return;
    if (noticeVisible) {
      this.finalSaleMessages.forEach((message) => {
        message.hidden = message.dataset.sizingFinalSaleMessage !== selectedValue;
      });
    }
    this.setFinalSaleNoticeVisibility(noticeVisible, animate);
  }
  setFinalSaleNoticeVisibility(isVisible, animate = false) {
    if (!this.finalSaleNotice) {
      return;
    }
    if (isVisible && animate && this.isInlinePresentation && this.inlineState === "open") {
      this.finalSaleNotice.hidden = false;
      const sticky = this.closest("safe-sticky.product-info");
      const layout = sticky?.parentElement;
      if (layout && getComputedStyle(sticky).position === "sticky") {
        if (!sticky.style.getPropertyValue("--rr-sizing-sticky-top")) {
          sticky.style.setProperty("--rr-sizing-sticky-top", sticky.getBoundingClientRect().top + "px");
        }
        const room = Math.max(0, sticky.getBoundingClientRect().top - layout.getBoundingClientRect().top);
        const message = this.finalSaleMessages.find((item) => !item.hidden);
        const gap = 1.75 * parseFloat(getComputedStyle(document.documentElement).fontSize);
        const currentNoticeHeight = this.finalSaleNotice.getBoundingClientRect().height +
          parseFloat(getComputedStyle(this.finalSaleNotice).marginBlockStart);
        const growth = Math.max(0, (message?.scrollHeight || 0) + gap - currentNoticeHeight);
        layout.style.setProperty("--rr-sizing-sticky-room", Math.max(layout.clientHeight, sticky.offsetHeight + growth + room + 1) + "px");
      }
    }
    this.finalSaleNoticeShouldBeVisible = isVisible;
    if (this.finalSaleNoticeHideTimer) {
      window.clearTimeout(this.finalSaleNoticeHideTimer);
      this.finalSaleNoticeHideTimer = null;
    }
    this.finalSaleNotice.setAttribute("aria-hidden", isVisible ? "false" : "true");
    if (isVisible) {
      this.finalSaleNotice.hidden = false;
      if (animate) {
        requestAnimationFrame(() => {
          if (this.finalSaleNoticeShouldBeVisible) {
            this.finalSaleNotice?.classList.add("is-visible");
          }
        });
      } else {
        this.finalSaleNotice.classList.add("is-visible");
      }
      return;
    }
    this.finalSaleNotice.classList.remove("is-visible");
    if (animate && !this.finalSaleNotice.hidden) {
      this.finalSaleNoticeHideTimer = window.setTimeout(() => {
        if (
          !this.finalSaleNoticeShouldBeVisible &&
          !this.finalSaleNotice?.classList.contains("is-visible")
        ) {
          this.finalSaleNotice.hidden = true;
          this.finalSaleMessages.forEach((message) => {
            message.hidden = true;
          });
        }
        this.finalSaleNoticeHideTimer = null;
      }, 520);
    } else {
      this.finalSaleNotice.hidden = true;
      this.finalSaleMessages.forEach((message) => {
        message.hidden = true;
      });
    }
  }
  showError() {
    if (this.errorMessage) {
      this.errorMessage.hidden = false;
    }
  }
  applyChoiceToForm(selectedChoice) {
    if (!this.sizingPropertyInput || !this.assistancePropertyInput) {
      return;
    }
    if (selectedChoice.hasAttribute("data-sizing-assistance")) {
      this.sizingPropertyInput.value = "";
      this.sizingPropertyInput.disabled = true;
      this.assistancePropertyInput.value = "Requested";
      this.assistancePropertyInput.disabled = false;
    } else {
      const selectedSize = this.dataset.sizingType === "ring" ? `US ${selectedChoice.value}` : `${selectedChoice.value} in`;
      this.sizingPropertyInput.value = selectedSize;
      this.sizingPropertyInput.disabled = false;
      this.assistancePropertyInput.value = "";
      this.assistancePropertyInput.disabled = true;
    }
  }
  confirmSelection() {
    if (this.isClosing) {
      return;
    }
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    if (!selectedChoice) {
      return;
    }
    if (this.mode === "update") {
      this.updateCartSelection(selectedChoice);
      return;
    }
    if (!this.sizingPropertyInput || !this.assistancePropertyInput) {
      return;
    }
    this.applyChoiceToForm(selectedChoice);
    this.confirmedChoice = selectedChoice;
    this.giaRevealPending = true;
    this.giaRevealWasAdding = false;
    this.prepareInlineAddingState();
    this.close(true);
  }
  async updateCartSelection(selectedChoice) {
    if (this.isUpdating) {
      return;
    }
    this.isUpdating = true;
    this.confirmButton?.setAttribute("aria-busy", "true");
    this.updateConfirmState();
    if (this.errorMessage) {
      this.errorMessage.hidden = true;
    }
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let cartContent;
    try {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      if (!cartResponse.ok) {
        throw new Error("Unable to retrieve the current cart.");
      }
      const currentCart = await cartResponse.json();
      const variantId = Number(this.form?.querySelector('[name="id"]')?.value);
      const currentLineItem = this.updateLineKey
        ? currentCart.items.find((item) => item.key === this.updateLineKey)
        : currentCart.items.find((item) => Number(item.variant_id) === variantId);
      if (!currentLineItem) {
        throw new Error("The selected cart line is no longer available.");
      }
      const properties = { ...(currentLineItem.properties || {}) };
      delete properties["Requested ring size"];
      delete properties["Requested Ring Size"];
      delete properties["Ring Size"];
      delete properties["Requested wrist size"];
      delete properties["Request Bracelet Size"];
      delete properties["Requested Bracelet Size"];
      delete properties["Bracelet Size"];
      delete properties["Sizing assistance"];
      delete properties["Sizing Assistance"];
      delete properties["_Resizing Final Sale Acknowledged"];
      if (selectedChoice.hasAttribute("data-sizing-assistance")) {
        properties["Sizing Assistance"] = "Requested";
      } else if (this.dataset.sizingType === "bracelet") {
        properties["Bracelet Size"] = `${selectedChoice.value} in`;
      } else {
        properties["Ring Size"] = `US ${selectedChoice.value}`;
      }
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          id: currentLineItem.key,
          quantity: currentLineItem.quantity,
          properties,
          sections: sectionsToBundle.join(",")
        })
      });
      cartContent = await response.json();
      if (!response.ok) {
        throw new Error(cartContent.description || "Unable to update the selected size.");
      }
      rrCartSizingOrder.promote(cartContent, currentLineItem, properties);
    } catch (error) {
      console.error("[Product sizing editor]", error);
      cartContent = null;
      this.showError();
    } finally {
      this.isUpdating = false;
      this.confirmButton?.removeAttribute("aria-busy");
      this.updateConfirmState();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
    if (!cartContent) {
      return;
    }
    this.confirmedChoice = selectedChoice;
    this.applyChoiceToForm(selectedChoice);
    const cartDrawer = document.querySelector("cart-drawer");
    const cartDrawerRefreshed = cartDrawer ? new Promise((resolve) => {
      const timeoutId = setTimeout(resolve, 2e3);
      cartDrawer.addEventListener("cart-drawer:refreshed", () => {
        clearTimeout(timeoutId);
        resolve();
      }, { once: true });
    }) : Promise.resolve();
    await this.close();
    document.documentElement.dispatchEvent(
      new CustomEvent("rr:sizing-selection-committed", {
        bubbles: true,
        detail: { variantId: this.currentVariantId }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "line-item:change",
          cart: cartContent
        }
      })
    );
    await cartDrawerRefreshed;
    cartDrawer?.show();
  }
};
if (!window.customElements.get("product-sizing-modal")) {
  window.customElements.define("product-sizing-modal", ProductSizingModal);
}

var CartRingSizingModal = class extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.dialog = null;
    this.openButton = null;
    this.choiceInputs = [];
    this.confirmedChoice = null;
    this.confirmButton = null;
    this.errorMessage = null;
    this.sizingType = "ring";
    this.finalSaleNotice = null;
    this.finalSaleMessages = [];
    this.finalSaleNoticeHideTimer = null;
    this.finalSaleNoticeShouldBeVisible = false;
    this.isClosing = false;
    this.isUpdating = false;
    this.isNavigating = false;
  }
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const lineItem = this.closest("line-item");
    this.sizingType = this.dataset.sizingType === "bracelet" ? "bracelet" : "ring";
    this.dialog = this.querySelector("dialog");
    this.openButton = lineItem?.querySelector("[data-cart-ring-sizing-open]");
    this.choiceInputs = Array.from(this.querySelectorAll("[data-cart-sizing-size], [data-cart-sizing-assistance]"));
    this.confirmedChoice = this.choiceInputs.find((input) => input.checked) || null;
    this.confirmButton = this.querySelector("[data-cart-sizing-confirm]");
    this.errorMessage = this.querySelector("[data-cart-sizing-error]");
    this.finalSaleNotice = this.querySelector("[data-sizing-final-sale-notice]");
    this.finalSaleMessages = Array.from(this.querySelectorAll("[data-sizing-final-sale-message]"));
    this.openButton?.addEventListener("click", () => this.open(), { signal });
    this.choiceInputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.updateFinalSaleNoticeState(true);
        this.updateConfirmState();
      }, { signal });
    });
    this.querySelector("[data-cart-sizing-close]")?.addEventListener("click", () => this.close(), { signal });
    this.confirmButton?.addEventListener("click", () => this.confirmSelection(), { signal });
    this.dialog?.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    this.dialog?.addEventListener("click", (event) => {
      if (event.target === this.dialog) {
        this.close();
      }
    }, { signal });
    this.updateFinalSaleNoticeState();
    this.updateConfirmState();
  }
  disconnectedCallback() {
    this.sizingSentenceToken = (this.sizingSentenceToken || 0) + 1;
    this.sizingSentenceAnimation?.cancel();
    this.abortController?.abort();
    if (this.finalSaleNoticeHideTimer) {
      window.clearTimeout(this.finalSaleNoticeHideTimer);
    }
    if (this.dialog?.open) {
      this.dialog.close();
    }
  }
  open() {
    const cartDrawer = this.closest("cart-drawer");
    if (cartDrawer && this.dataset.productUrl) {
      this.navigateToProductSizing(cartDrawer);
      return;
    }
    if (!this.dialog || this.isUpdating) {
      return;
    }
    this.isClosing = false;
    this.restoreConfirmedChoice();
    if (this.errorMessage) {
      this.errorMessage.hidden = true;
    }
    this.updateFinalSaleNoticeState();
    this.updateConfirmState();
    if (!this.dialog.open) {
      this.dialog.showModal();
    }
    this.dialog.scrollTop = 0;
    this.openButton?.setAttribute("aria-expanded", "true");
    requestAnimationFrame(() => {
      this.dialog.classList.add("is-visible");
      const selectedChoice = this.choiceInputs.find((input) => input.checked);
      (selectedChoice || this.choiceInputs[0])?.focus({ preventScroll: true });
    });
  }
  async navigateToProductSizing(cartDrawer) {
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;
    this.openButton?.setAttribute("aria-busy", "true");
    const targetUrl = new URL(this.dataset.productUrl, window.location.origin);
    targetUrl.searchParams.set("variant", this.dataset.variantId);
    targetUrl.searchParams.set("rr_sizing", "update");
    targetUrl.searchParams.set("rr_sizing_key", this.dataset.lineKey);
    try {
      await cartDrawer.hide();
    } finally {
      window.location.assign(targetUrl.href);
    }
  }
  async close(restoreFocus = true) {
    if (!this.dialog?.open || this.isClosing) {
      return;
    }
    this.isClosing = true;
    this.dialog.classList.remove("is-visible");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    await new Promise((resolve) => window.setTimeout(resolve, reduceMotion ? 0 : 180));
    if (this.dialog.open) {
      this.dialog.close();
    }
    this.openButton?.setAttribute("aria-expanded", "false");
    this.isClosing = false;
    this.restoreConfirmedChoice();
    if (restoreFocus) {
      this.openButton?.focus({ preventScroll: true });
    }
  }
  restoreConfirmedChoice() {
    this.choiceInputs.forEach((input) => {
      input.checked = input === this.confirmedChoice;
    });
    this.updateFinalSaleNoticeState();
    this.updateConfirmState();
  }
  updateConfirmState() {
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    if (this.confirmButton) {
      this.confirmButton.disabled = this.isUpdating || !selectedChoice;
    }
  }
  isCustomSizeChoice(choice) {
    return Boolean(choice) && !choice.hasAttribute("data-cart-sizing-assistance") && choice.value !== this.dataset.standardSize;
  }
  updateFinalSaleNoticeState(animate = false) {
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    const noticeVisible = this.isCustomSizeChoice(selectedChoice);
    const selectedValue = noticeVisible ? selectedChoice.value : "";
    if (rrFadeSizingSentence(this, selectedValue, animate)) return;
    if (noticeVisible) {
      this.finalSaleMessages.forEach((message) => {
        message.hidden = message.dataset.sizingFinalSaleMessage !== selectedValue;
      });
    }
    this.setFinalSaleNoticeVisibility(noticeVisible, animate);
  }
  setFinalSaleNoticeVisibility(isVisible, animate = false) {
    if (!this.finalSaleNotice) {
      return;
    }
    this.finalSaleNoticeShouldBeVisible = isVisible;
    if (this.finalSaleNoticeHideTimer) {
      window.clearTimeout(this.finalSaleNoticeHideTimer);
      this.finalSaleNoticeHideTimer = null;
    }
    this.finalSaleNotice.setAttribute("aria-hidden", isVisible ? "false" : "true");
    if (isVisible) {
      this.finalSaleNotice.hidden = false;
      if (animate) {
        requestAnimationFrame(() => {
          if (this.finalSaleNoticeShouldBeVisible) {
            this.finalSaleNotice?.classList.add("is-visible");
          }
        });
      } else {
        this.finalSaleNotice.classList.add("is-visible");
      }
      return;
    }
    this.finalSaleNotice.classList.remove("is-visible");
    if (animate && !this.finalSaleNotice.hidden) {
      this.finalSaleNoticeHideTimer = window.setTimeout(() => {
        if (
          !this.finalSaleNoticeShouldBeVisible &&
          !this.finalSaleNotice?.classList.contains("is-visible")
        ) {
          this.finalSaleNotice.hidden = true;
          this.finalSaleMessages.forEach((message) => {
            message.hidden = true;
          });
        }
        this.finalSaleNoticeHideTimer = null;
      }, 520);
    } else {
      this.finalSaleNotice.hidden = true;
      this.finalSaleMessages.forEach((message) => {
        message.hidden = true;
      });
    }
  }
  showError() {
    if (this.errorMessage) {
      this.errorMessage.hidden = false;
    }
  }
  async confirmSelection() {
    const selectedChoice = this.choiceInputs.find((input) => input.checked);
    const lineKey = this.dataset.lineKey;
    if (!selectedChoice || !lineKey || this.isUpdating) {
      return;
    }
    this.isUpdating = true;
    this.confirmButton?.setAttribute("aria-busy", "true");
    this.updateConfirmState();
    if (this.errorMessage) {
      this.errorMessage.hidden = true;
    }
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let cartContent;
    try {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      if (!cartResponse.ok) {
        throw new Error("Unable to retrieve the current cart.");
      }
      const currentCart = await cartResponse.json();
      const currentLineItem = currentCart.items.find((item) => item.key === lineKey);
      if (!currentLineItem) {
        throw new Error("The selected cart line is no longer available.");
      }
      const properties = { ...(currentLineItem.properties || {}) };
      delete properties["Requested ring size"];
      delete properties["Requested Ring Size"];
      delete properties["Ring Size"];
      delete properties["Requested wrist size"];
      delete properties["Request Bracelet Size"];
      delete properties["Requested Bracelet Size"];
      delete properties["Bracelet Size"];
      delete properties["Sizing assistance"];
      delete properties["Sizing Assistance"];
      delete properties["_Resizing Final Sale Acknowledged"];
      if (selectedChoice.hasAttribute("data-cart-sizing-assistance")) {
        properties["Sizing Assistance"] = "Requested";
      } else if (this.sizingType === "bracelet") {
        properties["Bracelet Size"] = `${selectedChoice.value} in`;
      } else {
        properties["Ring Size"] = `US ${selectedChoice.value}`;
      }
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          id: currentLineItem.key,
          quantity: currentLineItem.quantity,
          properties,
          sections: sectionsToBundle.join(",")
        })
      });
      cartContent = await response.json();
      if (!response.ok) {
        throw new Error(cartContent.description || "Unable to update the selected size.");
      }
      rrCartSizingOrder.promote(cartContent, currentLineItem, properties);
    } catch (error) {
      console.error("[Cart product sizing]", error);
      cartContent = null;
      this.showError();
    } finally {
      this.isUpdating = false;
      this.confirmButton?.removeAttribute("aria-busy");
      this.updateConfirmState();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
    if (!cartContent) {
      return;
    }
    this.confirmedChoice = selectedChoice;
    await this.close(false);
    if (window.themeVariables.settings.pageType === "cart") {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "line-item:change",
          cart: cartContent
        }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("rr:sizing-selection-committed", {
        bubbles: true,
        detail: { variantId: Number(this.dataset.variantId) }
      })
    );
  }
};
if (!window.customElements.get("cart-ring-sizing-modal")) {
  window.customElements.define("cart-ring-sizing-modal", CartRingSizingModal);
}
